import axios from 'axios';

// API base URL - should NOT include /api suffix
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/public/interviews`,
  timeout: 30000,
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 404) {
      throw new Error('Session not found');
    } else if (error.response?.status === 410) {
      throw new Error('Session has expired or been completed');
    } else if (error.response?.status === 409) {
      throw new Error('Invalid session state for this operation');
    } else if (error.response?.data?.error?.message) {
      throw new Error(error.response.data.error.message);
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
);

export const interviewApi = {
  // Get interview session info
  getSessionInfo: async (sessionToken) => {
    const response = await api.get(`/${sessionToken}`);
    return response.data.data;
  },

  // Start interview
  startInterview: async (sessionToken, formData) => {
    const response = await api.post(`/${sessionToken}/start`, formData);
    return response.data.data;
  },

  // Get all questions
  getQuestions: async (sessionToken) => {
    const response = await api.get(`/${sessionToken}/questions`);
    return response.data.data;
  },

  // Get current question
  getCurrentQuestion: async (sessionToken) => {
    const response = await api.get(`/${sessionToken}/current`);
    return response.data.data;
  },

  // Submit answer - Always use FormData as per API documentation
  submitAnswer: async (sessionToken, formData) => {
    const response = await api.post(`/${sessionToken}/answers`, formData);
    return response.data.data;
  },

  // Get session progress
  getProgress: async (sessionToken) => {
    const response = await api.get(`/${sessionToken}/progress`);
    return response.data.data;
  },

  // Get interaction history
  getHistory: async (sessionToken, page = 1, pageSize = 20) => {
    const response = await api.get(`/${sessionToken}/history`, {
      params: { page, page_size: pageSize },
    });
    return response.data.data;
  },

  // Poll upload status for a submitted answer (async upload flow)
  // Call every 2-3s while upload_pending is true; stop when video_ready/audio_ready is true
  pollUploadStatus: async (sessionToken, responseId) => {
    const response = await api.get(`/${sessionToken}/answers/${responseId}/upload-status`);
    return response.data.data;
  },

  // Start direct-to-storage chunked upload for a response
  startChunkedUpload: async (sessionToken, responseId, payload) => {
    const response = await api.post(`/${sessionToken}/answers/${responseId}/upload/start`, payload);
    return response.data.data;
  },

  // Get presigned URL for an upload part
  getChunkedUploadPartUrl: async (sessionToken, responseId, params) => {
    const response = await api.get(`/${sessionToken}/answers/${responseId}/upload/part`, { params });
    return response.data.data;
  },

  // Complete chunked upload and persist media URL to answer
  completeChunkedUpload: async (sessionToken, responseId, payload) => {
    const response = await api.post(`/${sessionToken}/answers/${responseId}/upload/complete`, payload);
    return response.data.data;
  },

  // Abort unfinished chunked upload (cleanup)
  abortChunkedUpload: async (sessionToken, responseId, payload) => {
    const response = await api.post(`/${sessionToken}/answers/${responseId}/upload/abort`, payload);
    return response.data.data;
  },

  // Complete interview
  completeInterview: async (sessionToken, completionData = {}) => {
    const response = await api.post(`/${sessionToken}/complete`, completionData);
    return response.data.data;
  },
};

export default api;

// http://localhost:3002/api/public/interviews/UTP5IB
// http://localhost:3002/api/public/interview/UTP5IB